import React from 'react';
import { skeleton, cn } from './uiTheme';

/**
 * shadcn-style Skeleton component.
 * Use to show a placeholder while content is loading.
 * @see https://ui.shadcn.com/docs/components/skeleton
 */
function Skeleton({ className = '', ...props }: any) {
  return <div className={cn(skeleton.block, 'rounded-md', className)} {...props} />;
}

export default Skeleton;
export { Skeleton };

/**
 * Page header: title + subtitle placeholders
 */
export function PageHeaderSkeleton() {
  return (
    <div className="mb-6">
      <Skeleton className="h-8 w-48 mb-2" />
      <Skeleton className="h-4 w-72 max-w-full" />
    </div>
  );
}

/**
 * Single stat card placeholder (icon + label + value)
 */
export function StatCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-dark-border bg-dark-surface shadow-sm">
      <div className="p-5">
        <div className="flex items-center">
          <Skeleton className="h-10 w-10 rounded-md flex-shrink-0" />
          <div className="ml-5 flex-1 min-w-0">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-8 w-16" />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Grid of stat card placeholders
 */
export function StatsGridSkeleton({ count = 4, className = '' }: any) {
  const gridClass = count <= 2 ? 'card-grid-2-mdl' : 'card-grid-4-mdl';
  return (
    <div className={`${gridClass} ${className}`.trim()}>
      {Array.from({ length: count }).map((_, i) => (
        <StatCardSkeleton key={i} />
      ))}
    </div>
  );
}

/**
 * Simple stats row (e.g. 3 cards in a row)
 */
export function StatsRowSkeleton({ count = 3 }: any) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 section-block-mdl">
      {Array.from({ length: count }).map((_, i) => (
        <StatCardSkeleton key={i} />
      ))}
    </div>
  );
}

/**
 * Search bar placeholder
 */
export function SearchBarSkeleton() {
  return (
    <div className="mb-4">
      <Skeleton className="h-10 w-full rounded-md" />
    </div>
  );
}

/**
 * Table placeholder: optional header row + body rows
 */
export function TableSkeleton({ cols = 5, rows = 8, showHeader = true }: any) {
  return (
    <div className="overflow-hidden rounded-xl border border-dark-border bg-dark-surface shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-dark-border">
          {showHeader && (
            <thead className="bg-dark-elevated">
              <tr>
                {Array.from({ length: cols }).map((_, i) => (
                  <th key={i} className="px-6 py-3">
                    <Skeleton className="h-4 w-20" />
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody className="divide-y divide-dark-border bg-dark-surface">
            {Array.from({ length: rows }).map((_, r) => (
              <tr key={r}>
                {Array.from({ length: cols }).map((_, c) => (
                  <td key={c} className="px-6 py-4">
                    <Skeleton className="h-4 w-full max-w-[120px]" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Full page layout: header + search + table (for list/table pages)
 */
export function TablePageSkeleton({ title = true, search = true, cols = 5, rows = 8 }: any) {
  return (
    <div className="px-4 py-6 sm:px-0">
      {title && <PageHeaderSkeleton />}
      {search && <SearchBarSkeleton />}
      <TableSkeleton cols={cols} rows={rows} />
    </div>
  );
}

/**
 * Table page with stats cards: header + stats grid + search + table
 */
export function TablePageWithStatsSkeleton({ statCount = 5, cols = 5, rows = 8 }: any) {
  return (
    <div className="px-4 py-6 sm:px-0">
      <PageHeaderSkeleton />
      <div className="section-block-mdl">
        <StatsGridSkeleton count={statCount} />
      </div>
      <SearchBarSkeleton />
      <TableSkeleton cols={cols} rows={rows} />
    </div>
  );
}

/**
 * Dashboard: header + two stat grids + content block
 */
export function DashboardSkeleton() {
  return (
    <div className="px-4 py-6 sm:px-0">
      <PageHeaderSkeleton />
      <div className="card-grid-4-mdl section-block-mdl">
        {[1, 2, 3, 4].map((i) => (
          <StatCardSkeleton key={i} />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 section-block-mdl">
        {[1, 2, 3].map((i) => (
          <StatCardSkeleton key={i} />
        ))}
      </div>
      <div className="overflow-hidden rounded-xl border border-dark-border bg-dark-surface shadow-sm">
        <div className="p-5">
          <Skeleton className="h-6 w-40 mb-4" />
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center justify-between">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Single card skeleton matching members/streams/projects/repos card layout:
 * rounded icon, title, subtitle, arrow; border-t; pill badges.
 */
export function CardSkeleton() {
  return (
    <div className="rounded-xl border border-dark-border bg-dark-surface shadow-sm p-4">
      <div className="flex items-center gap-3 mb-3">
        <Skeleton className="h-10 w-10 flex-shrink-0 rounded-full" />
        <div className="min-w-0 flex-1 space-y-1.5">
          <Skeleton className="h-4 w-32 max-w-[80%]" />
          <Skeleton className="h-3 w-24" />
        </div>
        <Skeleton className="h-5 w-5 flex-shrink-0 rounded" />
      </div>
      <div className="flex flex-wrap gap-2 text-sm border-t border-dark-border pt-3">
        <Skeleton className="h-7 w-20 rounded-lg" />
        <Skeleton className="h-7 w-16 rounded-lg" />
        <Skeleton className="h-7 w-24 rounded-lg" />
      </div>
    </div>
  );
}

/**
 * Grid of card skeletons (matches browse list pages: cards-grid-mdl-dense).
 */
export function CardGridSkeleton({ count = 6, className = '' }: any) {
  return (
    <div className={`cards-grid-mdl-dense ${className}`.trim()}>
      {Array.from({ length: count }).map((_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
  );
}

/**
 * Page toolbar skeleton: title + optional right-area placeholder
 */
export function PageToolbarSkeleton() {
  return (
    <div className="mb-6">
      <div className="mb-2">
        <Skeleton className="h-4 w-64 max-w-full" />
      </div>
      <div className="flex items-center justify-between gap-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-10 rounded-lg" />
      </div>
    </div>
  );
}

/**
 * Reports / Alerts: header + optional stats + grid of card skeletons
 */
export function CardListPageSkeleton({ statCount = 4, cardCount = 4, showStats = true }: any) {
  return (
    <div className="px-4 py-6 sm:px-0">
      <PageHeaderSkeleton />
      {showStats && statCount > 0 && (
        <div className="card-grid-4-mdl section-block-mdl">
          {Array.from({ length: statCount }).map((_, i) => (
            <StatCardSkeleton key={i} />
          ))}
        </div>
      )}
      <CardGridSkeleton count={cardCount} />
    </div>
  );
}

/**
 * Notifications list: header + list of notification-shaped rows
 */
export function NotificationsListSkeleton({ count = 5 }: any) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-lg border border-dark-border bg-dark-surface shadow-sm overflow-hidden"
        >
          <div className="px-4 py-3 sm:px-5">
            <div className="flex items-center gap-2 mb-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-24" />
            </div>
            <Skeleton className="h-4 w-3/4 mb-2" />
            <Skeleton className="h-3 w-full max-w-md" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Profile/settings: section cards with form-like lines
 */
export function ProfileSkeleton() {
  return (
    <div className="px-4 py-6 sm:px-0 max-w-3xl">
      <PageHeaderSkeleton />
      <div className="space-y-6">
        {[1, 2, 3].map((block) => (
          <div
            key={block}
            className="rounded-lg border border-dark-border bg-dark-surface shadow-sm overflow-hidden"
          >
            <div className="p-5 border-b border-dark-border">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-64 mt-1" />
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-10 w-full max-w-xs" />
              </div>
              <div>
                <Skeleton className="h-4 w-32 mb-2" />
                <Skeleton className="h-10 w-full max-w-sm" />
              </div>
              <Skeleton className="h-10 w-36" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Repository detail: back link + title + description + actions + content blocks
 */
export function RepositoryDetailSkeleton() {
  return (
    <div className="px-4 py-6 sm:px-0">
      <Skeleton className="h-4 w-32 mb-4" />
      <div className="flex items-start justify-between mb-6">
        <div>
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96 max-w-full" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-10 w-28" />
          <Skeleton className="h-10 w-24" />
        </div>
      </div>
      <div className="card-grid-4-mdl section-block-mdl">
        {[1, 2, 3, 4].map((i) => (
          <StatCardSkeleton key={i} />
        ))}
      </div>
      <TableSkeleton cols={4} rows={6} />
    </div>
  );
}
